import { motion } from 'framer-motion'
import { 
  Cloud, 
  Flame, 
  Activity, 
  Sprout, 
  Snowflake,
  Mountain,
  Wind,
  Cpu,
  Leaf,
  Gem,
  HelpCircle
} from 'lucide-react'
import { PlayerAction } from '../types/game'
import { useGameStore } from '../store/gameStore'

const ACTIONS = [
  {
    id: PlayerAction.MAKE_RAIN,
    name: '雨を降らせる',
    icon: Cloud,
    color: 'from-blue-400 to-blue-600',
    energyCost: 10,
    cooldownTime: 30
  },
  {
    id: PlayerAction.TRIGGER_VOLCANO,
    name: '火山噴火',
    icon: Flame,
    color: 'from-red-500 to-orange-600',
    energyCost: 30,
    cooldownTime: 120
  },
  {
    id: PlayerAction.TRIGGER_EARTHQUAKE,
    name: '地震',
    icon: Activity,
    color: 'from-yellow-500 to-amber-600',
    energyCost: 25,
    cooldownTime: 90
  },
  {
    id: PlayerAction.BOOST_LIFE,
    name: '生命を育む',
    icon: Sprout,
    color: 'from-green-400 to-emerald-600',
    energyCost: 15,
    cooldownTime: 45
  },
  {
    id: PlayerAction.MELT_ICE,
    name: '氷を溶かす',
    icon: Snowflake,
    color: 'from-cyan-400 to-blue-500',
    energyCost: 20,
    cooldownTime: 60
  },
  {
    id: PlayerAction.RAISE_LAND,
    name: '陸地隆起',
    icon: Mountain,
    color: 'from-stone-500 to-stone-700',
    energyCost: 35,
    cooldownTime: 150
  },
  {
    id: PlayerAction.CREATE_STORM,
    name: '嵐を起こす',
    icon: Wind,
    color: 'from-purple-400 to-indigo-600',
    energyCost: 18,
    cooldownTime: 50
  },
  {
    id: PlayerAction.ADVANCE_TECH,
    name: '技術進化',
    icon: Cpu,
    color: 'from-pink-400 to-purple-600',
    energyCost: 25,
    cooldownTime: 100
  },
  {
    id: PlayerAction.PURIFY_ENVIRONMENT,
    name: '環境浄化',
    icon: Leaf,
    color: 'from-teal-400 to-green-600',
    energyCost: 20,
    cooldownTime: 70
  },
  {
    id: PlayerAction.GENERATE_RESOURCES,
    name: '資源生成',
    icon: Gem,
    color: 'from-amber-400 to-yellow-600',
    energyCost: 30,
    cooldownTime: 80
  }
]

interface ActionPanelProps {
  onActionComplete?: () => void
  onQuizOpen?: () => void
}

export function ActionPanel({ onActionComplete, onQuizOpen }: ActionPanelProps) {
  const { 
    executeAction, 
    playerEnergy, 
    cooldowns, 
    selectedAction,
    selectAction 
  } = useGameStore()
  
  const handleAction = async (action: PlayerAction) => {
    selectAction(action)
    await executeAction(action)
    setTimeout(() => {
      selectAction(null)
      onActionComplete?.()
    }, 2000)
  }
  
  return (
    <div className="glass-panel p-6">
      <h2 className="text-xl font-bold mb-4 text-cyan-300">アクション</h2>
      
      <div className="grid grid-cols-2 gap-3">
        {ACTIONS.map((action) => {
          const Icon = action.icon
          const cooldown = cooldowns[action.id] || 0
          const isDisabled = cooldown > 0 || playerEnergy < action.energyCost
          const isSelected = selectedAction === action.id
          
          return (
            <motion.button
              key={action.id}
              onClick={() => handleAction(action.id)}
              disabled={isDisabled}
              className={`
                relative p-3 rounded-lg transition-all duration-200
                ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}
                ${isSelected ? 'ring-2 ring-white' : ''}
                bg-gradient-to-r ${action.color}
              `}
              whileHover={!isDisabled ? { scale: 1.05 } : {}}
              whileTap={!isDisabled ? { scale: 0.95 } : {}}
            >
              <div className="flex items-center space-x-2">
                <Icon className="w-5 h-5" />
                <div className="text-left flex-1">
                  <div className="text-sm font-medium">{action.name}</div>
                  <div className="text-xs opacity-75">
                    エネルギー: {action.energyCost}
                  </div>
                </div>
              </div>
              
              {cooldown > 0 && (
                <div className="absolute inset-0 bg-black/60 rounded-lg overflow-hidden">
                  {/* カウントダウン表示 */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`font-bold text-2xl ${
                      cooldown <= 5 ? 'text-yellow-300 animate-pulse' : 'text-white'
                    }`}>
                      {cooldown}秒
                    </span>
                  </div>
                  
                  {/* 下部のプログレスバー */}
                  <div className="absolute bottom-0 left-0 right-0 h-2 bg-black/50">
                    <div 
                      className={`h-full transition-all duration-1000 ease-linear ${
                        cooldown <= 5 
                          ? 'bg-gradient-to-r from-yellow-400 to-orange-500' 
                          : 'bg-gradient-to-r from-gray-400 to-gray-500'
                      }`}
                      style={{
                        width: `${((action.cooldownTime - cooldown) / action.cooldownTime) * 100}%`
                      }}
                    />
                  </div>
                </div>
              )}
            </motion.button>
          )
        })}
      </div>
      
      {/* エネルギーバー */}
      <div className="mt-6">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-cyan-300">エネルギー</span>
          <span className="text-white font-bold">{Math.floor(playerEnergy)}/10000</span>
        </div>
        <div className="h-6 bg-gray-700 rounded-full overflow-hidden relative">
          <motion.div 
            className="h-full bg-gradient-to-r from-green-400 to-emerald-500"
            initial={{ width: 0 }}
            animate={{ width: `${(playerEnergy / 10000) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs text-white font-bold drop-shadow">
              {Math.floor((playerEnergy / 10000) * 100)}%
            </span>
          </div>
        </div>
      </div>
      
      {/* クイズボタン */}
      <div className="mt-4">
        <button
          onClick={onQuizOpen}
          className="w-full action-button bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 flex items-center justify-center space-x-2"
        >
          <HelpCircle className="w-5 h-5" />
          <span>知識クイズに挑戦</span>
        </button>
      </div>
    </div>
  )
}